// src/components/admin/PayrollManagement.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DollarSign, Download, Calendar, CheckCircle, Clock, Users, Briefcase, Shield, FileText } from 'lucide-react';
import { api } from '../../services/api';
import PayrollSummary from './payroll/PayrollSummary';
import PayrollList from './payroll/PayrollList';
import PayrollProcess from './payroll/PayrollProcess';
import EmployeeCompensationBlocks from './compensation/EmployeeCompensationBlocks';
import PayGroupsTab from './compensation/PayGroupsTab';
import PFManagement from './PFManagement';
import TDSManagement from './TDSManagement';

const PayrollManagement = () => {
    const [activeTab, setActiveTab] = useState('compensation'); // 'compensation', 'pay-groups', 'pf', 'tds', or 'processing'
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [payrollData, setPayrollData] = useState({
        summary: {},
        records: [],
        pending: []
    });
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [showProcessModal, setShowProcessModal] = useState(false);

    useEffect(() => {
        if (activeTab === 'processing') {
            fetchPayrollData();
        }
    }, [selectedMonth, selectedYear, activeTab]);

    const fetchPayrollData = async () => {
        try {
            const response = await api.get(`/admin/payroll?month=${selectedMonth}&year=${selectedYear}`);
            setPayrollData(response.data);
        } catch (error) {
            console.error('Error fetching payroll data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProcessPayroll = async () => {
        try {
            await api.post('/admin/payroll/process', {
                month: selectedMonth,
                year: selectedYear
            });
            setShowProcessModal(false);
            fetchPayrollData();
        } catch (error) {
            console.error('Error processing payroll:', error);
        }
    };

    const handleApprovePayroll = async (payrollId) => {
        try {
            await api.post(`/admin/payroll/${payrollId}/approve`);
            fetchPayrollData();
        } catch (error) {
            console.error('Error approving payroll:', error);
        }
    };

    const handleExportPayroll = async () => {
        try {
            const response = await api.get(`/admin/payroll/export?month=${selectedMonth}&year=${selectedYear}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payroll_${selectedYear}_${selectedMonth}.xlsx`);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error exporting payroll:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Tabs */}
            <div className="tour-comp-tabs border-b border-gray-200 dark:border-gray-700">
                <div className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('compensation')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                            activeTab === 'compensation'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        <Users className="w-5 h-5" />
                        Employee Compensation
                    </button>
                    <button
                        onClick={() => setActiveTab('pay-groups')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                            activeTab === 'pay-groups'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        <DollarSign className="w-5 h-5" />
                        Pay Groups
                    </button>
                    <button
                        onClick={() => setActiveTab('pf')}
                        className={`tour-comp-tab-pf pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                            activeTab === 'pf'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        <Shield className="w-5 h-5" />
                        PF Management
                    </button>
                    <button
                        onClick={() => setActiveTab('tds')}
                        className={`tour-comp-tab-tds pb-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                            activeTab === 'tds'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                        <FileText className="w-5 h-5" />
                        TDS Management
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'compensation' && (
                <EmployeeCompensationBlocks />
            )}

            {activeTab === 'pay-groups' && (
                <PayGroupsTab />
            )}

            {activeTab === 'pf' && (
                <PFManagement />
            )}

            {activeTab === 'tds' && (
                <TDSManagement />
            )}

            {activeTab === 'processing' && (
                <>
                    {loading ? (
                        <div className="flex justify-center items-center h-64">Loading...</div>
                    ) : (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payroll Processing</h1>
                                <div className="flex gap-2">
                                    <select
                                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                    >
                                        {[...Array(12)].map((_, i) => (
                                            <option key={i + 1} value={i + 1}>
                                                {new Date(2025, i).toLocaleString('default', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    >
                                        {[2024, 2025, 2026].map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={handleExportPayroll}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Export
                                    </button>
                                    <button
                                        onClick={() => setShowProcessModal(true)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                    >
                                        <DollarSign className="w-4 h-4" />
                                        Process Payroll
                                    </button>
                                </div>
                            </div>

                            {/* Payroll Summary */}
                            <PayrollSummary summary={payrollData.summary} />

                            {/* Pending Approvals */}
                            {payrollData.pending?.length > 0 && (
                                <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Pending Approvals</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                        {payrollData.pending.length} payroll records require approval
                                    </p>
                                    <button
                                        onClick={() => handleApprovePayroll('all')}
                                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                                    >
                                        Approve All
                                    </button>
                                </div>
                            )}

                            {/* Payroll Records */}
                            <PayrollList
                                records={payrollData.records}
                                onApprove={handleApprovePayroll}
                            />

                            {/* Process Payroll Modal */}
                            {showProcessModal && (
                                <PayrollProcess
                                    month={selectedMonth}
                                    year={selectedYear}
                                    onClose={() => setShowProcessModal(false)}
                                    onProcess={handleProcessPayroll}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PayrollManagement;